import { Button } from '@dixa/lib-shared-habla';
import noop from 'lodash/noop';
import { useEffect, useState } from 'react';
import { useEvent, useMountedState } from 'react-use';

import { facebookMessengerApi } from 'apis/facebookMessenger';
import { id } from 'utils/id';

import styles from './facebookLoginButton.module.scss';
import { IFrameMessage } from './types/facebookIframeMessage.types';
import { FacebookLoginButtonProps } from './types/facebookLoginButton.types';

/** The url of the facebook login page. */
const facebookLoginUrl = 'https://facebook-login.dixa.io';

/**
 * A button component that authenticates with Facebook when pressed.
 *
 * `How it works`: This internally creates an iframe with the Dixa Facebook
 * login page spanning the clickable area of the button. When the user clicks
 * the button, the iframe intercepts the event, which in turn triggers the
 * Facebook login flow of opening the dialog. Throughout, the iframe
 * communicates events back to the parent window in order to e.g. change the
 * button state.
 *
 * `Implementation Rationale`: We run the Facebook SDK it in an iframe on our
 * own domain so that we don't have to register every allowed customer domain
 * manually in the Facebook Developer Settings. In addition this has a
 * limitation of 50 domains. When running it in a different window, the Facebook
 * dialog can only be opened based on user interaction. Thus the user has to
 * click directly into the iframe - otherwise the browser will block the dialog
 * and the user would have to give permissions.
 */
export const FacebookLoginButton = ({
  onSuccess,
  onError,
  scope,
  platform,
  children,
}: FacebookLoginButtonProps): JSX.Element => {
  const [isInitializing, setIsInitializing] = useState(true);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  /** A flag that is used to re-render the iframe */
  const [renderIframe, setRenderIframe] = useState(true);
  const [componentId] = useState(id());
  const [iframeSrc, setIframeSrc] = useState<string>();
  const mounted = useMountedState();
  const { data: applicationsResponse } =
    facebookMessengerApi.useReadApplicationsQuery();

  // Sets up the listener for receiving incoming messages from the iframe.
  useEvent('message', (event: MessageEvent<IFrameMessage>) => {
    if (
      event.origin === facebookLoginUrl &&
      event.data.componentId === componentId
    ) {
      switch (event.data.type) {
        case 'initialized':
          setIsInitializing(false);
          break;
        case 'authenticating':
          setIsAuthenticating(true);
          break;
        case 'authenticated':
          setIsAuthenticating(false);
          onSuccess(event.data.arg);
          break;
        case 'closed':
          /**
           * In order to fix a bug where the Facebook login modal closes
           * immediately after it's opened, we set this flag off, and then on
           * again in the next render cycle, which removes the iframe and adds
           * it again to the DOM, and that reloads the Facebook SDK.
           */
          setRenderIframe(false);
          setTimeout(() => {
            setRenderIframe(true);
            setIsAuthenticating(false);
            setIsInitializing(true);
          });
          break;
        case 'error':
          setIsAuthenticating(false);
          onError?.(event.data.arg);
          break;
      }
    }
  });

  // Awaits the common application from the api and sets the iframe source.
  useEffect(() => {
    const commonApplication = applicationsResponse?.data.applications.find(
      (application) => application.type === 'common',
    );

    if (commonApplication && mounted()) {
      setIframeSrc(
        `${facebookLoginUrl}?${new URLSearchParams({
          scope,
          componentId,
          appId: commonApplication.id,
          origin: location.origin,
          platform,
        })}`,
      );
    }
  }, [applicationsResponse, componentId, platform, scope, mounted]);

  return (
    <Button
      disabled={isInitializing || isAuthenticating}
      className={styles.button}
      onClick={noop}
      variant="primary"
    >
      <>
        <div className={styles.content}>{children}</div>
        {iframeSrc && renderIframe && (
          <iframe src={iframeSrc} className={styles.iframe}></iframe>
        )}
      </>
    </Button>
  );
};
